import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Play, AlertCircle, Check, Copy, ExternalLink } from 'lucide-react';
import { useWebhooks } from '../hooks/useWebhooks';

interface Webhook {
  id: string;
  name: string;
  url: string;
  events: string[];
  active: boolean;
  secret: string;
  created_at: string;
  last_triggered?: string;
  success_rate?: number;
}

export function WebhookSettings() {
  const { webhooks, loading, error, createWebhook, deleteWebhook, updateWebhook, testWebhook } = useWebhooks();
  const [showNewForm, setShowNewForm] = useState(false);
  const [newWebhook, setNewWebhook] = useState({
    name: '',
    url: '',
    events: [] as string[],
    secret: ''
  });

  const availableEvents = [
    'aluno.cadastro',
    'aluno.atualizacao',
    'aluno.matricula',
    'email.aberto',
    'email.clicado',
    'curso.iniciado',
    'curso.concluido',
    'atividade.realizada'
  ];

  const handleCreateWebhook = async () => {
    if (!newWebhook.name || !newWebhook.url || newWebhook.events.length === 0) {
      alert('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    try {
      await createWebhook(newWebhook);
      setShowNewForm(false);
      setNewWebhook({ name: '', url: '', events: [], secret: '' });
    } catch (error) {
      console.error('Erro ao criar webhook:', error);
      alert('Erro ao criar webhook. Por favor, tente novamente.');
    }
  };

  const copySecret = (secret: string) => {
    navigator.clipboard.writeText(secret);
    alert('Secret copiado para a área de transferência!');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-background-card rounded w-1/4"></div>
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="h-24 bg-background-card rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
        <AlertCircle className="w-5 h-5" />
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Webhooks</h2>
          <p className="text-sm text-gray-400">Gerencie integrações via webhooks</p>
        </div>
        <button
          onClick={() => setShowNewForm(true)}
          className="px-4 py-2 bg-[#268c73] text-white rounded-lg flex items-center gap-2 hover:bg-[#1a7a61] transition-colors"
        >
          <Plus className="w-4 h-4" />
          Novo Webhook
        </button>
      </div>

      {/* Formulário de novo webhook */}
      {showNewForm && (
        <div className="bg-background-card p-6 rounded-lg mb-6">
          <h3 className="text-lg font-medium text-white mb-4">Novo Webhook</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-2">Nome</label>
              <input
                type="text"
                value={newWebhook.name}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, name: e.target.value }))}
                className="w-full bg-background text-white rounded-lg px-4 py-2 border border-border focus:border-[#268c73] focus:outline-none"
                placeholder="Ex: Integração CRM"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">URL</label>
              <input
                type="url"
                value={newWebhook.url}
                onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                className="w-full bg-background text-white rounded-lg px-4 py-2 border border-border focus:border-[#268c73] focus:outline-none"
                placeholder="https://api.exemplo.com/webhook"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-2">Eventos</label>
              <div className="grid grid-cols-2 gap-4">
                {availableEvents.map(event => (
                  <label key={event} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={newWebhook.events.includes(event)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewWebhook(prev => ({
                            ...prev,
                            events: [...prev.events, event]
                          }));
                        } else {
                          setNewWebhook(prev => ({
                            ...prev,
                            events: prev.events.filter(e => e !== event)
                          }));
                        }
                      }}
                      className="form-checkbox h-4 w-4 text-[#268c73] bg-background border-border rounded"
                    />
                    <span className="text-gray-300">{event}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowNewForm(false)}
                className="px-4 py-2 bg-background text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateWebhook}
                className="px-4 py-2 bg-[#268c73] text-white rounded-lg hover:bg-[#1a7a61] transition-colors"
              >
                Criar Webhook
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de webhooks */}
      <div className="space-y-4">
        {webhooks.map((webhook) => (
          <div key={webhook.id} className="bg-background-card p-6 rounded-lg">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-medium text-white">{webhook.name}</h3>
                <p className="text-sm text-gray-400 mt-1">{webhook.url}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => testWebhook(webhook.id)}
                  className="p-2 bg-background text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
                  title="Testar webhook"
                >
                  <Play className="w-4 h-4" />
                </button>
                <button
                  onClick={() => copySecret(webhook.secret)}
                  className="p-2 bg-background text-gray-300 rounded-lg hover:bg-background-hover transition-colors"
                  title="Copiar secret"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Tem certeza que deseja excluir este webhook?')) {
                      deleteWebhook(webhook.id);
                    }
                  }}
                  className="p-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-colors"
                  title="Excluir webhook"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {webhook.events.map(event => (
                <span
                  key={event}
                  className="px-2 py-1 bg-[#268c73]/10 text-[#268c73] rounded text-sm"
                >
                  {event}
                </span>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t border-border">
              <div>
                <p className="text-sm text-gray-400">Status</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className={`w-2 h-2 rounded-full ${webhook.active ? 'bg-green-400' : 'bg-gray-400'}`}></div>
                  <p className="text-white">{webhook.active ? 'Ativo' : 'Inativo'}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-400">Última execução</p>
                <p className="text-white mt-1">
                  {webhook.last_triggered 
                    ? new Date(webhook.last_triggered).toLocaleString('pt-BR')
                    : 'Nunca executado'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Taxa de sucesso</p>
                <p className="text-white mt-1">
                  {webhook.success_rate ? `${webhook.success_rate}%` : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        ))}

        {webhooks.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-400">Nenhum webhook configurado</p>
            <button
              onClick={() => setShowNewForm(true)}
              className="mt-4 text-[#268c73] hover:text-[#1a7a61] transition-colors"
            >
              Criar primeiro webhook
            </button>
          </div>
        )}
      </div>

      {/* Documentação */}
      <div className="mt-8 bg-background-card rounded-lg p-6">
        <h3 className="text-lg font-medium text-white mb-4">Documentação</h3>
        <p className="text-gray-400 mb-4">
          Os webhooks permitem que você receba notificações em tempo real sobre eventos do sistema.
          Cada webhook recebe um payload JSON com informações detalhadas sobre o evento.
        </p>
        <a
          href="/documentation#webhooks"
          className="text-[#268c73] hover:text-[#1a7a61] transition-colors flex items-center gap-2"
        >
          Ver documentação completa
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}